import React, { memo, useMemo, useState, useRef, useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";

import AppArrowIcon from "components/icons/app-arrow-icon";
import AppProfileModal from "components/app-profile-modal";
import AppUploadAvatarModal from "components/app-upload-avatar-modal";
import AppUpdatePasswordModal from "./pages/dashboard/app-update-password-modal";
import notificationIcon from "assets/images/components/app-header/notification-icon.svg";
import api from "services/api";
import pathnames from "routes/pathnames";
import serveRequestErrors from "common/serve-request-errors";
import classNames from "common/class-names";
import { formatDatePattern } from "common/calendar";
import AppNotificationModal from "./app-notification-modal";

const AppHeader = ({ title = "", backTitle = "Back to lists", ...props }) => {
	const profile = useSelector((state) => state.profile);
	const navigate = useNavigate();
	const profileModalRef = useRef();
	const updatePasswordRef = useRef();
	const uploadAvatarRef = useRef();
	const updateNotificationRef = useRef();
	const [anchorEl, setAnchorEl] = useState(null);
	const profileName = useMemo(() => profile?.nickName?.split(" ")?.[0]?.[0] || "E", [profile.nickName]);
	const [unreadCount, setUnreadCount] = useState(0);
	const location = useLocation();
	const isDashboard = useMemo(() => location.pathname === pathnames.dashboard, [location.pathname]);


	const today = useMemo(() => {
		const now = new Date();
		return `${now.toLocaleString("en-us", { weekday: "long" })}, ${formatDatePattern(now)}`;
	}, []);

	const greeting = useMemo(() => {
		const now = new Date();
		const currentHour = now.getHours();
		let text = "";

		if (currentHour < 12) {
			text = "Good Morning";
		} else if (currentHour < 18) {
			text = "Good Afternoon";
		} else {
			text = "Good Evening";
		}

		return `${text}, ${profile?.fullName || "Employee"}`;
	}, [profile?.fullName]);

	const onhandleCloseMenu = useCallback(() => {
		setAnchorEl(null);
	}, []);

	const onHandleOpenMenu = useCallback((event) => {
		setAnchorEl(event.target);
	}, []);

	const onHandleChangePassword = useCallback(() => {
		onhandleCloseMenu();
		updatePasswordRef.current.onHandleShow();
	}, [onhandleCloseMenu]);

	const onHandleViewProfile = useCallback(() => {
		onhandleCloseMenu();
		profileModalRef.current.onHandleShow();
	}, [onhandleCloseMenu]);

	const onHandleUploadAvatar = useCallback(() => {
		onhandleCloseMenu();
		uploadAvatarRef.current.onHandleShow();
	}, [onhandleCloseMenu]);

	const onHandleOpenNotification = useCallback(() => {
		updateNotificationRef.current.onHandleShow();
	}, []);

	const onHandleGetUnreadNotification = useCallback(async () => {
		let response = null;

		try {
			const params = { portalType: "EMPLOYEE" };
			response = await api.get.notification.getUnread(params);
			setUnreadCount(response);
		} catch (error) {
			serveRequestErrors(error);
		}
	}, []);

	useEffect(() => {
		onHandleGetUnreadNotification();
	}, [location.pathname, onHandleGetUnreadNotification]);

	const onUnreadChange = useCallback((updater) => {
		setUnreadCount((prev) => (typeof updater === "function" ? updater(prev) : updater));
	}, []);

	const notificationBellClassNames = classNames({
		header__notification: true,
		"header__notification--unread": unreadCount > 0,
	});

	return (
		<header className="app-header">
			<div className="header">
				<button id="header-menu" className="header__menu" onClick={props.onHandleToggleSideNav} />
				{isDashboard && (
					<div className="header__greeting">
						<h1 className="header__title">{greeting}</h1>
						<p className="header__date">{today}</p>
					</div>
				)}
				{!isDashboard && title && <h1 className="header__title">{title}</h1>}
				{!isDashboard && !title && (
					<button className="header__back" onClick={() => navigate(-1)}>
						<div className="header__back-icon">
							<AppArrowIcon color="#018897" />
						</div>
						{backTitle}
					</button>
				)}

				<div className="header__profile">
					<div className="header__image" onClick={onHandleOpenMenu}>
						{!profile.avatar && <p className="header__name">{profileName}</p>}
						{profile.avatar && <img src={profile.avatar} alt="profile" />}
					</div>

					<div className="header__content">
						<p className="header__name">{profile.nickName || "Employee"}</p>
						<p className="header__text">{profile?.role?.roleNameEn}</p>
					</div>

					<div className={notificationBellClassNames} onClick={onHandleOpenNotification} data-count={unreadCount}>
						<img src={notificationIcon} alt="notifications" />
					</div>
				</div>
			</div>

			{/* prettier-ignore */}
			<Menu classes={{ root: "app-header-menu" }} anchorEl={anchorEl} open={!!anchorEl} onClose={onhandleCloseMenu} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}>
                <MenuItem onClick={onHandleViewProfile}>
					<p className="app-header-menu__text">Profile</p>
				</MenuItem>
                <MenuItem onClick={onHandleUploadAvatar}>
					<p className="app-header-menu__text">Change Photo</p>
				</MenuItem>
                <MenuItem onClick={onHandleChangePassword}>
					<p className="app-header-menu__text">Change Password</p>
				</MenuItem>
            </Menu>

			<AppProfileModal ref={profileModalRef} />
			<AppUploadAvatarModal ref={uploadAvatarRef} />
			<AppUpdatePasswordModal email={profile.email} ref={updatePasswordRef} />
			<AppNotificationModal ref={updateNotificationRef} onUnreadChange={onUnreadChange} />
		</header>
	);
};

export default memo(AppHeader);
